import { Calendar, Mail, Phone, Clock } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Separator } from "@/components/ui/Separator";
import { useState } from "react";
import CommentsSection from "./CommentSectionServeTicket";
import { useTicket } from "@/context/TicketContext";
import { useSelector } from "react-redux";
import { RootState } from "@/app/store";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/MobileToolTip";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import UserSidePanel from "../user-add/AddUserToTicket";
import { useGetCounterListQuery } from "@/services/CounterApi";
import { useUpdateThresholdMutation } from "@/services/ServiceApis";
import { TicketAction, TicketActionOnStatus, useActivetRSeriesMutation } from "@/services/LiveQueueService";
import { calculateWaitingTime, formatDate, formatTime } from "@/utils/Helper";
import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/AlertDialog";
import { Input } from "@/components/ui/Input";
import { Controller, useForm } from "react-hook-form";
import { Ticket } from "lucide-react";
import { formatPhoneNumber } from '@/utils/Helper';
import TaskForm from "@/components/admin/tasks/component/modal/TaskForm";
import { Task, TaskPriority, TaskStatus } from "@/app/types/tasks";
import { toast } from "@/components/ui/UseToast";
import LinkedTickets from "./LinkTickets";
import RouteTicket from "./RouteTicket";
import DetailLogs from "@/components/admin/tickets/ticket-detail/AuditLog";
interface NowServingCardProps {
  handleRetry: () => void;
  isCounterOpen:boolean;
  eventId?: number;
}
interface Ticket {
  id: number;
  ticket_id: string;
}

interface AutoCompleteOption {
  value: string;
  label: string;
}
const NowServingCard: React.FC<NowServingCardProps> = ({ handleRetry,isCounterOpen, eventId }) => {
  const [openLog, setOpenLog] = useState(false);
  const [ticketId, setTicketId] = useState<undefined | Number>();
  const [action, setAction] = useState<"add" | "edit">("add");
  const [isLinkSheetOpen, setIsLinkSheetOpen] = useState(false);
  const [isRouteSheetOpen, setIsRouteSheetOpen] = useState(false);
  const [selectKey, setSelectKey] = useState(0); // Add this state to track when to reset the component
  const [newComment, setNewComment] = useState<string>("");

  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [selectedTicket, setSelectedTicket] =
    useState<AutoCompleteOption | null>(null);
  const [data, setData] = useState<Task | undefined>(undefined);

  const userInfo = useSelector((state: RootState) => state.auth.userInfo);
  const [startThreshold] = useUpdateThresholdMutation();
  const [updateDetail, setIsupdateDetail] = useState(false);
  const {
    startServing,
    currentTicket,
    loading,
    routeTicket,
    addDetails,
    handleTicketAction,
    updateLinkedTicket,
    isAccrossDepartment,
  } = useTicket();
  const [selectedAction, setSelectedAction] = useState<TicketAction | null>(
    null
  );

  // Step 2: Handle dropdown change
  const handleActionChange = (value: string) => {
    const selectedValue = value as TicketAction;
    setSelectedAction(selectedValue);
  };

  const defaultValues = {
    title: "",
    description: "",
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    defaultValues,
  });

  const onSubmit = async (data: { title: string; description?: string }) => {
    try {
      addDetails({
        title: data.title,
        description: data.description,
      });
      setIsupdateDetail(false);
    } catch (error) {}
  };

  const [showComments, setShowComments] = useState(true); // State for comments section

  const { data: counterOptions, refetch: countersRefetch } =
    useGetCounterListQuery(
      {
        query: (isAccrossDepartment
          ? "?sort_key=id&sort_order=asc&status=active"
          : `?sort_key=id&sort_order=asc&status=active&department_id=${userInfo?.department_id}`)
          + (eventId ? `&event_id=${eventId}` : ""),
      },
      {
        skip: !userInfo?.service_id,
        refetchOnMountOrArgChange: true,
      }
    );

  const [activateRSeries] = useActivetRSeriesMutation();
  const [isSheetOpen, setIsSheetOpen] = useState<boolean>(false);

  const handleOpenChange = () => {
    setIsSheetOpen((prev) => !prev);
  };

  const handleOpen = () => {
    const today = new Date();
    const localDate = today.toLocaleDateString("en-CA"); //
    setAction("edit");
    setIsDialogOpen(true);
    setData({
      is_deleted: false,
      task_id: 0,
      created_by: "",
      updated_by: "",
      comments: [],
      title: "",
      description: "",
      status: TaskStatus.TODO,
      due_date: localDate, // Format date as YYYY-MM-DD
      priority: TaskPriority.LOW,
      assigned_to: "",
      created_at: new Date().toISOString(),
      attachments: [],
      tags: [],
      linked_ticket: {
        ticket_id: Number(currentTicket?.id) || 0, // Convert to number and provide a default value of 0 if undefined
        ticket_number: currentTicket?.ticket_number?.toString() || "",
        reserved_number: "",
        assigned_number: "",
      },
    });
  };

  if (loading) {
    return (
      <div className="bg-secondary rounded-lg p-4 space-y-4 border-2 border-primary ">
        <div className="animate-pulse space-y-4">
          {/* Title */}
          <div className="flex flex-col items-center justify-between">
            <div className="h-4 w-32 bg-gray-300 rounded-md"></div>
            <div className="h-4 w-48 bg-gray-300 rounded-md mt-2"></div>
          </div>

          {/* Separator */}
          <Separator className="bg-primary" />

          {/* Ticket Info */}
          <div className="flex justify-between items-start">
            <div className="w-full flex flex-col items-start">
              <div className="h-4 w-24 bg-gray-300 rounded-md"></div>
              <div className="h-4 w-32 bg-gray-300 rounded-md mt-2"></div>
            </div>
          </div>

          {/* Counter & Serving Status */}
          <div className="flex justify-between items-start w-full">
            <div className="w-full">
              <div className="h-4 w-28 bg-gray-300 rounded-md"></div>
            </div>
            <div className="w-full">
              <div className="h-4 w-24 bg-[#C1554E] rounded-md"></div>
            </div>
          </div>

          <Separator className="bg-primary" />

          {/* Details */}
          <div className="space-y-3">
            <div className="flex gap-3 items-center text-sm">
              <Calendar className="h-4 w-4" />
              <div className="h-4 w-32 bg-gray-300 rounded-md"></div>
            </div>

            <div className="flex gap-3 items-center text-sm">
              <Mail className="h-4 w-4" />
              <div className="h-4 w-48 bg-gray-300 rounded-md"></div>
            </div>

            <div className="flex gap-3 items-center text-sm">
              <Phone className="h-4 w-4" />
              <div className="h-4 w-48 bg-gray-300 rounded-md"></div>
            </div>

            <div className="flex gap-3 items-center text-sm">
              <Clock className="h-4 w-4" />
              <div className="h-4 w-28 bg-gray-300 rounded-md"></div>
            </div>
          </div>

          <Separator className="bg-primary" />

          {/* Action Buttons */}
          <div className="flex justify-between gap-2 pt-4">
            <div className="flex flex-row gap-2">
              <div className="h-9 w-20 bg-gray-300 rounded-md"></div>
              <div className="h-9 w-40 bg-gray-300 rounded-md"></div>
            </div>
            <div className="h-9 w-20 bg-gray-300 rounded-md"></div>
          </div>

          {/* Recall Button */}
          <div className="flex justify-between pt-4 flex-row">
            <div className="h-9 w-20 bg-gray-300 rounded-md"></div>
          </div>

          {/* Comments Toggle */}
          <div className="flex items-center gap-2 text-[#C1554E] text-sm font-medium">
            <div className="h-4 w-6 bg-gray-300 rounded-md"></div>
            <div className="h-4 w-32 bg-gray-300 rounded-md"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!currentTicket) {
    return (
      <div className="bg-secondary rounded-lg p-4 space-y-4 border-2 border-primary">
        <div>No ticket is currently being served.</div>
        <div className="flex items-center justify-between">
          {isCounterOpen ? (
            <Button
              className="!hover:bg-primary !hover:text-black"
              onClick={() => {
                startServing();
                startThreshold(userInfo?.service_id as number);
                handleRetry();
                activateRSeries();
              }}
              disabled={loading}
            >
              Start Serving
            </Button>
          ) : (
            <span className=" rounded-md p-2 text-black">
              {" "}
              Open Counter First
            </span>
          )}
        </div>
        <UserSidePanel isOpen={isSheetOpen} setOpen={setIsSheetOpen} />
      </div>
    );
  }

  function readTicket(text: string) {
    const speech = new SpeechSynthesisUtterance();
    speech.text = text;
    speech.lang = "en-US";
    speech.volume = 1; // Adjust volume (0 to 1)
    speech.rate = 1.25; // Adjust speed (0.1 to 10)
    speech.pitch = 1; // Adjust pitch (0 to 2)

    window.speechSynthesis.speak(speech);
  }

  const handleButtonClick = async () => {
    if (!selectedAction) {
      toast({
        variant: "destructive",
        description: "Please select a status before clicking next",
      });
      return;
    }

    try {
      // Set local loading state if needed
      const actionToUse =
        selectedAction === TicketAction.NO_SHOW
          ? TicketAction.ABSENT
          : selectedAction;

      // Wait for the API call to complete
      await handleTicketAction(actionToUse, setSelectedAction, newComment);
      setSelectKey((prev) => prev + 1);
    } catch (error) {
      // Show error toast if needed
      toast({
        variant: "destructive",
        description: "Failed to update ticket status",
      });
      // Don't reset selection on error so user can try again
    }
  };

  return (
    <div className="bg-secondary rounded-lg p-2 space-y-2 border-2 border-primary">
      <div className="space-y-1">
        <div className="flex  items-center justify-between">
          <div
            onClick={handleOpenChange}
            className="text-sm md:text-sm lg:text-base font-bold flex flex-row gap-2 items-center underline cursor-pointer"
          >
            Ticket No: {currentTicket.number}{" "}
            <div>
              <button onClick={handleOpenChange} className="curson-pointer">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      {String(currentTicket.user_id) === "0" ? (
                        <i
                          className="fa-solid fa-pencil text-base"
                          style={{ color: "#808080" }}
                          onClick={handleOpenChange}
                        ></i>
                      ) : (
                        <i
                          className="fa-solid fa-pencil text-base"
                          style={{ color: "#808080" }}
                          onClick={handleOpenChange}
                        ></i>
                      )}
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                      {String(currentTicket.user_id) === "0" ? "Add" : "Edit"}{" "}
                      Ticket Detail
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </button>
            </div>
          </div>
          <div className="flex flex-row gap-2">
            <Select
              key={selectKey}
              value={selectedAction || undefined}
              onValueChange={handleActionChange}
            >
              <SelectTrigger className="Capitalize w-full justify-center flex">
                <SelectValue
                  placeholder="Select a status"
                  className="text-gray-400 w-fit"
                ></SelectValue>
              </SelectTrigger>
              <SelectContent className="flex justify-center items-center">
                {Object.values(TicketActionOnStatus).map((action) => (
                  <SelectItem
                    key={action}
                    value={action}
                    className="text-center flex "
                  >
                    <span className="flex items-center justify-center w-full">
                      <span className={`rounded-[50%] w-4 h-4 mr-2`}></span>
                      <span className="text-sm">
                        {action.replace(/_/g, " ")}
                      </span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="w-full">
              <Button onClick={handleButtonClick} className="text-nowrap">
                {loading ? "Updating" : "Next"}
              </Button>
            </div>
          </div>
        </div>

        <Separator className="bg-primary" />
        <div className="flex justify-between items-start">
          <div className="w-full flex flex-col items-start">
            <div className="flex flex-row items-center justify-between w-full">
              <div className="flex items-center w-fit">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <p className="text-xl text-[#d0423e] font-bold truncate max-w-[400px]">
                        {currentTicket?.title ?? ""}
                      </p>
                    </TooltipTrigger>
                    <TooltipContent className="capitalize max-w-[250px] w-fit break-words whitespace-normal text-black">
                      {currentTicket?.title ?? ""}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div className="flex items-center space-x-1">
                <Button
                  variant="ghost"
                  className="text-sm h-9 w-9 p-0 text-black"
                >
                  {" "}
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <i
                          onClick={() => setIsRouteSheetOpen(true)}
                          className="fa-solid fa-share text-base"
                          style={{ color: "#808080" }}
                        ></i>
                      </TooltipTrigger>
                      <TooltipContent>Route a tcket</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </Button>

                <Button
                  variant="ghost"
                  className="text-sm h-9 w-9 p-0 text-black"
                  onClick={() =>
                    readTicket(
                      `Ticket Number ${currentTicket.number}, please visit counter number ${userInfo?.counter_id}`
                    )
                  }
                >
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <i
                          className="fa-solid fa-volume-high text-base"
                          style={{ color: "#808080" }}
                        ></i>
                      </TooltipTrigger>
                      <TooltipContent>Recall</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </Button>
                <Button
                  variant="ghost"
                  className="text-sm h-9 w-9 p-0 text-black"
                  onClick={handleOpen}
                >
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <i
                          className="fa-solid fa-list-check text-base"
                          style={{ color: "#808080" }}
                        ></i>
                      </TooltipTrigger>
                      <TooltipContent>Add Task for ticket</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </Button>
              </div>
            </div>
            <div className="flex flex-col justify-between border-2 rounded-lg bg-[#fff3a2] w-[90%] p-4 border-primary ">
              <div className="text-sm font-medium text-gray-500 flex flex-row justify-between items-center">
                <div>
                  ITS :{" "}
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="text-sm text-black">
                          {String(currentTicket.user_id) === "0"
                            ? ""
                            : currentTicket.user_details?.username?.length > 40
                              ? `${currentTicket.user_details.username.slice(0, 12)}...`
                              : currentTicket.user_details.username}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        {currentTicket.user_details?.username}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <i
                        onClick={() => setIsLinkSheetOpen(true)}
                        className="fas fa-share-square text-base hover:cursor-pointer"
                        style={{ color: "#808080" }}
                      ></i>
                    </TooltipTrigger>
                    <TooltipContent>Link a Ticket</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div className="text-sm font-medium text-gray-500">
                Customer Name :{" "}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="text-sm text-black">
                        {String(currentTicket.user_id) === "0"
                          ? ""
                          : currentTicket.user_details?.first_name
                            ? (
                                currentTicket.user_details.first_name +
                                " " +
                                (currentTicket.user_details.last_name || "")
                              ).length > 40
                              ? `${(currentTicket.user_details.first_name + " " + (currentTicket.user_details.last_name || "")).slice(0, 12)}...`
                              : currentTicket.user_details.first_name +
                                " " +
                                (currentTicket.user_details.last_name || "")
                            : currentTicket.user_details?.username || ""}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      {currentTicket.user_details?.username}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div className="text-sm font-medium text-gray-500">
                Email :{" "}
                <span className="text-sm text-black">
                  {String(currentTicket.user_id) === "0"
                    ? ""
                    : currentTicket.user_details.email}
                </span>
              </div>

              <div className="text-sm font-medium text-gray-500">
                Phone Number :{" "}
                <span className="text-sm text-black">
                  {String(currentTicket.user_id) === "0"
                    ? ""
                    : formatPhoneNumber(currentTicket.user_details.phone_num)}
                </span>
              </div>

              <div className="text-sm font-medium text-gray-500">
                Ticket Date & Time:
                <span className="text-sm text-black">
                  {new Date(currentTicket?.date ?? "").toLocaleDateString("en-PK", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })
                .replace(/\//g, "-")}
                </span>
                <span className="text-sm text-black ml-2">
                  {formatTime(currentTicket.time ?? "")}
                </span>
              </div>
              <div className="text-sm font-medium text-gray-500">
                Ticket Id :{" "}
                <span className="text-sm text-black">
                  {" "}
                  {currentTicket.ticket_number}
                </span>
              </div>
              <div className="text-sm font-medium text-gray-500 ">
                Linked Ticket :{" "}
                <span
                  onClick={() => {
                    setTicketId(Number(currentTicket?.parent_ticket_id));
                    setOpenLog(true);
                  }}
                  className="text-sm text-black cursor-pointer underline underline-blue-500"
                >
                  {currentTicket.parent_ticket_number ?? ""}
                </span>
              </div>
              <div className="text-sm font-medium text-gray-500">
                Counter No :{" "}
                <span className="text-sm text-black">
                  {userInfo?.counter_id}
                </span>
              </div>
              <div className="text-sm font-medium text-gray-500">
                Waiting Time : {""}
                <span className="text-sm text-black">
                  {currentTicket.served_at
                    ? (() => {
                        const waitingMinutes = calculateWaitingTime(
                          currentTicket?.date,
                          currentTicket?.time,
                          currentTicket.served_at
                        );
                        return waitingMinutes >= 60
                          ? `${Math.floor(waitingMinutes / 60)} hr${waitingMinutes % 60 !== 0 ? ` ${waitingMinutes % 60} min` : ""}`
                          : waitingMinutes > 0
                            ? `${waitingMinutes} min`
                            : "0 min";
                      })()
                    : ""}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col justify-between border-2 rounded-lg bg-[#fff3a2] w-[90%] p-4 border-primary">
        <div className="text-sm font-medium text-gray-500">
          Description :{" "}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-sm text-black break-words whitespace-pre-line">
                  {currentTicket?.description ?? ""}
                </span>
              </TooltipTrigger>
              <TooltipContent className="capitalize max-w-[250px] w-fit break-words whitespace-normal text-black">
                {currentTicket?.description ?? ""}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
      <button
        className="flex items-center gap-2 text-[#C1554E] text-base font-medium"
        onClick={() => setShowComments(!showComments)}
      >
        <i
          className={`fa-solid  ${showComments ? "fa-angle-up" : "fa-angle-down"}`}
          style={{ color: "#d9540d" }}
        ></i>
        Show Comments
      </button>
      {showComments && <CommentsSection ticketId={currentTicket.id} newComment={newComment} setNewComment={setNewComment} />}
      <UserSidePanel isOpen={isSheetOpen} setOpen={setIsSheetOpen} />

      {updateDetail && (
        <AlertDialog
          open={updateDetail}
          onOpenChange={() => setIsupdateDetail(false)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogDescription>
                <div className="flex flex-col items-start justify-start gap-4">
                  <Ticket className="size-14" />
                  <AlertDialogTitle className="text-lg font-medium">
                    Please Provide the following details for Ticket
                  </AlertDialogTitle>
                </div>
                <form
                  onSubmit={handleSubmit(onSubmit)}
                  className="mt-4 space-y-4 w-full"
                >
                  <Controller
                    name="title"
                    rules={{ required: "Required" }}
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <div className="w-full">
                        <label
                          htmlFor="title"
                          className="block text-sm font-semibold text-start"
                        >
                          Title
                        </label>
                        <Input
                          type="text"
                          className="w-full px-3 py-2 border rounded-lg"
                          placeholder="Enter title"
                          value={value || ""}
                          onChange={onChange}
                        />
                      </div>
                    )}
                  />

                  <div className="space-y-2 w-full">
                    <label
                      htmlFor="description"
                      className="block text-sm font-semibold text-start"
                    >
                      Description
                    </label>
                    <Controller
                      control={control}
                      name="description"
                      render={({ field }) => (
                        <textarea
                          {...field}
                          placeholder="Enter description"
                          className="w-full border rounded p-2"
                        />
                      )}
                    />
                    {errors.description && (
                      <p className="text-red-500 text-xs text-start">
                        {errors.description.message?.toString()}
                      </p>
                    )}
                  </div>

                  <div className="flex justify-end items-center gap-3 mt-4">
                    <AlertDialogCancel
                      onClick={() => {
                        setIsupdateDetail(false);
                        reset({
                          title: "",
                          description: "", // Optional field
                        });
                      }}
                      disabled={loading}
                      className="w-[100px] border border-primary font-semibold"
                    >
                      <i className="fa fa-xmark text-base px-1"></i> No
                    </AlertDialogCancel>
                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-[140px] bg-primary text-black font-semibold"
                    >
                      <i className="fa-regular fa-floppy-disk text-base px-1"></i>{" "}
                      {loading ? "Updating..." : "Update"}
                    </Button>
                  </div>
                </form>
              </AlertDialogDescription>
            </AlertDialogHeader>
          </AlertDialogContent>
        </AlertDialog>
      )}

      <TaskForm
        isOpen={isDialogOpen}
        setOpen={setIsDialogOpen}
        action={action}
        setAction={setAction}
        data={data}
        setData={setData}
        refetch={() => console.log("")}
        isgettingCreate={true}
        initialTicket={{
          ticket_id: currentTicket?.id.toString() || "0", // Convert to number and provide a default value of 0 if undefined
          ticket_number: currentTicket?.ticket_number?.toString() || "",
        }}
      />
      <LinkedTickets isOpen={isLinkSheetOpen} setOpen={setIsLinkSheetOpen} />
      <RouteTicket isOpen={isRouteSheetOpen} setOpen={setIsRouteSheetOpen} />
      <DetailLogs isOpen={openLog} setOpen={setOpenLog} ticketId={ticketId} />
    </div>
  );
};

export default NowServingCard;
